import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Coerce } from '@kalidea/kaligraphi';
import { KalTableDatasource } from 'src/app/_shared/modules/kal-table/classes/kal-table-datasource';
import { tap } from 'rxjs/operators';
import { Subscription } from 'rxjs';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'kal-paginator',
    templateUrl: './kal-paginator.component.html',
    styleUrls: ['./kal-paginator.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class KalPaginatorComponent implements OnInit, OnDestroy {

    static readonly defaultPageSize = 50;
    static readonly defaultPagesToDisplay = 7;
    static readonly defaultRowsByPageList = [
        50,
        100,
        500,
        1000,
        5000,
        10000,
        1000000
    ];

    pageSizeControl: UntypedFormControl;

    @Input() datasource: KalTableDatasource<any>;

    // Page courante , 0 indexed
    @Input() current = 0;

    // Nombre d'élément par page
    @Input() size = KalPaginatorComponent.defaultPageSize;

    // lorsque le nombre d'élement par page change, on émet un évènement
    // on peut utiliser le 2-way binding grâce à ce nom : <kal-paginator [(size)]="size">
    @Output() readonly sizeChange = new EventEmitter<number>();

    @Output() readonly pageChange = new EventEmitter<number>();

    // Nombre d'éléments max
    @Input() count: number;

    // Nombre de page à afficher
    @Input() pagesToDisplay = KalPaginatorComponent.defaultPagesToDisplay;

    @Input() rowsByPageList = KalPaginatorComponent.defaultRowsByPageList;

    private subscriptionsList: Subscription[] = [];

    constructor(private cdr: ChangeDetectorRef) {
    }

    /**
     * le "rayon" ( en nombre de page ) à afficher de chaque côté de la page courante
     */
    get wing() {
        return Math.floor(this.pagesToDisplay / 2);
    }

    /**
     * indexé à partir de 0
     */
    get lastPage() {
        return Math.ceil(this.count / this.size) - 1;
    }

    /**
     * récupère la liste des numéros de page à afficher
     * ces numéros de page sont indexés à partir de 0
     */
    get pagesList() {
        const pages: number[] = [];

        const {lastPage, current, wing} = this;

        let start = Math.max(0, current - wing); // 3 pages avant la page courante, minimum 1
        // dans le cas où on est vers les dernières pages du listing, il faut décaller la première page affichée
        if (current + wing > lastPage) {
            // par exemple, si la page courante est 22 et que la dernière est 24, il faut démarrer à 24 - (3*2)
            start = Math.min(start, (lastPage - (2 * wing)));
            // et il faut s'assurer que start est bien > 1
            if (start < 0) {
                start = 0;
            }
        }

        for (let i = start; i < start + this.pagesToDisplay; i++) {
            if (i <= this.lastPage) {
                pages.push(i);
            }
        }

        return pages;
    }

    /*
     * @param page 0 indexed
     */
    goTo(page: number) {
        this.current = page;
        this.cdr.markForCheck();
        this.datasource?.page?.set({number: page, size: this.size});
        if (!this.datasource) {
            this.pageChange.emit(page);
        }
    }

    next() {
        this.goTo(this.current + 1);
    }

    prev() {
        this.goTo(this.current - 1);
    }

    private initSubscription() {
        // si y'a un datasource fourni, on va écouter les changements de pagination
        if (this.datasource) {

            const watchPageChanges$ = this.datasource.page.change$.pipe(
                tap(value => {
                    ({size: this.size, number: this.current} = value);
                    this.pageSizeControl.patchValue(this.size, {emitEvent: false});

                    this.cdr.markForCheck();
                })
            );
            this.subscriptionsList.push(watchPageChanges$.subscribe());
        }

        // on va écouter les changements de nombre d'éléments affichés par page
        const watchPageSiteChanges$ = this.pageSizeControl.valueChanges.pipe(
            tap(value => this.sizeChange.emit(value)),
            tap(size => {
                // if datasource, update pagination
                this.datasource?.page?.set({number: 0, size});
            })
        );
        this.subscriptionsList.push(watchPageSiteChanges$.subscribe());
    }

    ngOnInit(): void {

        this.pageSizeControl = new UntypedFormControl(this.size);

        this.initSubscription();

    }

    ngOnDestroy(): void {
        this.subscriptionsList.forEach(sub => sub?.unsubscribe());
    }

}
